import {
    ComponentFactoryResolver,
    ComponentRef,
    Injector,
    NgZone,
    StaticProvider,
    ViewContainerRef,
} from '@angular/core';
import { fromEventPattern, merge, Subscription } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';

import { CMPS_SCORECARD_WIDGET } from '../scorecard.metadata';

import { Layout, LayoutSize, TemplateConfig } from './config.scorecard.model';
import { ScLayoutContainerComponent } from './layout-container.component';
import { Widget } from './template-definition.model';
import { WidgetTypeComponentMapType } from './widget-type-component-map-type';

export interface InflateRef {
    itemType: 'row' | 'container' | 'widget';
    sizes: LayoutSize[];
    ref: ComponentRef<any>;
    children: InflateRef[];
}

export interface InflateHandle {
    refs: InflateRef[];
    sub: Subscription;
}

// TODO jss???
interface Styling {
    paddingRight: string;
    marginBottom: string;
}

function flatten<TIn, TOut = TIn>(
    arr: TIn[],
    nextIn: (i: TIn) => TIn[],
    nextOut: (i: TIn) => TOut[],
    flat: TOut[],
): TOut[] {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    arr &&
        arr.forEach((e) => {
            flat.push(...nextOut(e));
            return flatten(nextIn(e), nextIn, nextOut, flat);
        });
    return flat;
}

function _inflateRef(
    parentInjector: Injector,
    factoryResolver: ComponentFactoryResolver,
    viewContainerRef: ViewContainerRef,
    widgetTypeComponentMap: WidgetTypeComponentMapType,
    config: TemplateConfig,
    lt: Layout,
): InflateRef {
    const providers: StaticProvider[] = [];
    let widget: Widget;
    let comp: any;
    if (lt.itemType === 'container') {
        comp = ScLayoutContainerComponent;
    }
    if (lt.itemType === 'widget') {
        widget = config.widgets.find((e) => e.identifier === lt.identifier);

        if (!widget || !widget.enabled) {
            return null;
        }

        comp = widgetTypeComponentMap[widget.widgetType];

        providers.push({ provide: CMPS_SCORECARD_WIDGET, useValue: widget });
    }

    if (!comp) {
        return null;
    }

    const inj = Injector.create({
        providers,
        parent: parentInjector,
    });
    const ref = viewContainerRef.createComponent(
        factoryResolver.resolveComponentFactory(comp),
        null,
        inj,
    );
    return { itemType: lt.itemType, sizes: lt.sizes, ref, children: [] };
}

function createRow(
    parentInjector: Injector,
    factoryResolver: ComponentFactoryResolver,
    viewContainerRef: ViewContainerRef,
): InflateRef {
    const comp = factoryResolver.resolveComponentFactory(
        ScLayoutContainerComponent,
    );
    const rowRef = viewContainerRef.createComponent(comp, null, parentInjector);
    (rowRef.location.nativeElement as HTMLElement).setAttribute(
        'refId',
        Math.random().toString(),
    ); // TODO revise. @deprecated
    const conRef = rowRef.instance.viewContainerRef.createComponent(
        comp,
        null,
        parentInjector,
    );

    return {
        itemType: 'row',
        sizes: [{ breakpoint: 99999, width: '100%' }],
        ref: rowRef,
        children: [
            {
                itemType: 'container',
                sizes: [{ breakpoint: 99999, width: '100%' }],
                ref: conRef,
                children: [],
            },
        ],
    };
}

function getLayoutSize(sizes: LayoutSize[], breakpoint: number): LayoutSize {
    return (sizes ? [...sizes] : [{ breakpoint: 99999, width: '100%' }])
        .sort((a, b) => b.breakpoint - a.breakpoint)
        .reduce((p, c) => {
            if (!p || c.breakpoint > breakpoint) {
                return c;
            }
            return p;
        }, null);
}

function _inflate(
    parentInjector: Injector,
    factoryResolver: ComponentFactoryResolver,
    viewContainerRef: ViewContainerRef,
    widgetTypeComponentMap: WidgetTypeComponentMapType,
    config: TemplateConfig,
    layouts: Layout[],
    notRootLayout?: boolean,
): InflateRef[] {
    const refs: InflateRef[] = [];

    if (!layouts) {
        return refs;
    }

    // should this be sorted here?
    const ordered = [...layouts].sort(
        (a, b) => a.displayOrder - b.displayOrder,
    );

    let row: InflateRef;
    let rvcr: ViewContainerRef;
    let width = 0;

    ordered.forEach((c, ci) => {
        /* to accommodate page breaks when printing/exporting. only the root items of the template config layouts apply.
        this also means that each root item must sum up to 100, otherwise it gets complicated to adjust parent
        elements with page-break:avoid when there are nested elements with page-break:avoid */
        if (!notRootLayout) {
            if (ci === 0 || width >= 100) {
                row = createRow(
                    parentInjector,
                    factoryResolver,
                    viewContainerRef,
                );
                rvcr = (
                    row.children[0].ref.instance as ScLayoutContainerComponent
                ).viewContainerRef;
                refs.push(row);
                width = 0;
            }

            const ref = _inflateRef(
                parentInjector,
                factoryResolver,
                rvcr,
                widgetTypeComponentMap,
                config,
                c,
            );
            if (!ref) {
                return;
            }
            if (ref.itemType === 'container') {
                const vcr = (ref.ref.instance as ScLayoutContainerComponent)
                    .viewContainerRef;
                ref.children = _inflate(
                    parentInjector,
                    factoryResolver,
                    vcr,
                    widgetTypeComponentMap,
                    config,
                    c.children,
                    true,
                );
            }

            row.children.push(ref);

            const lts = getLayoutSize(ref.sizes, window.innerWidth);
            width += parseFloat(lts.width);
        }

        if (notRootLayout) {
            const ref = _inflateRef(
                parentInjector,
                factoryResolver,
                viewContainerRef,
                widgetTypeComponentMap,
                config,
                c,
            );
            if (!ref) {
                return;
            }
            if (ref.itemType === 'container') {
                const vcr = (ref.ref.instance as ScLayoutContainerComponent)
                    .viewContainerRef;
                ref.children = _inflate(
                    parentInjector,
                    factoryResolver,
                    vcr,
                    widgetTypeComponentMap,
                    config,
                    c.children,
                    true,
                );
            }
            refs.push(ref);
        }
    });

    return refs;
}

export function inflate(
    parentInjector: Injector,
    factoryResolver: ComponentFactoryResolver,
    viewContainerRef: ViewContainerRef,
    widgetTypeComponentMap: WidgetTypeComponentMapType,
    styling: Styling,
    config: TemplateConfig,
    isExporting: () => boolean,
    handle: (hdl: InflateHandle) => void,
): void {
    viewContainerRef.clear();

    if (!config || !config.layouts) {
        return;
    }

    const refs = _inflate(
        parentInjector,
        factoryResolver,
        viewContainerRef,
        widgetTypeComponentMap,
        config,
        config.layouts,
    );

    const slts = flatten<InflateRef, LayoutSize>(
        refs,
        (e) => e.children,
        (e) => e.sizes,
        [],
    );
    const mqls = slts
        .reduce((p, c) => {
            // eslint-disable-next-line @typescript-eslint/prefer-includes
            if (p.indexOf(c.breakpoint) === -1) {
                p.push(c.breakpoint);
            }
            return p;
        }, [])
        .map((e) => {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            const mql = window.matchMedia(`(min-width: ${e}px)`);
            return fromEventPattern(
                (handler) => mql.addListener(handler),
                (handler) => mql.removeListener(handler),
            );
        });
    const sub = merge(...mqls)
        .pipe(
            // get around browsers that apply a media query when printing via ctrl+p or window.print()
            filter(() => !isExporting || !isExporting()),
            take(1),
            // need to reinflate to recreate elements with page-break:avoid based on possible width changes
            tap(() => {
                const zone = parentInjector.get<NgZone>(NgZone);
                zone.run(() => {
                    deflate(hdl);
                    inflate(
                        parentInjector,
                        factoryResolver,
                        viewContainerRef,
                        widgetTypeComponentMap,
                        styling,
                        config,
                        isExporting,
                        handle,
                    );
                });
            }),
        )
        .subscribe();

    // layout immediately
    layout(refs, styling);

    const hdl = { refs, sub }; // so that mql observable can reference the original object

    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    handle && handle(hdl);
}

function layout(refs: InflateRef[], styling: Styling): void {
    let width = 0;
    refs.forEach((ref) => {
        layoutRef(ref, window.innerWidth);

        const el = ref.ref.location.nativeElement as HTMLElement;
        width += parseFloat(el.style.maxWidth);
        el.style.paddingRight = styling.paddingRight;
        if (width >= 100) {
            el.style.paddingRight = '0';
            width = 0;
        }
        // TODO revise
        if (ref.itemType === 'widget') {
            el.style.marginBottom = styling.marginBottom;
        }

        layout(ref.children, styling);
    });
}

function layoutRef(ref: InflateRef, breakpoint: number): void {
    const lts = getLayoutSize(ref.sizes, breakpoint);

    // TODO consider using jss to apply styles
    const el = ref.ref.location.nativeElement as HTMLElement;
    if (ref.itemType === 'row') {
        el.style.display = 'block';
        el.style.width = lts.width;
        el.style.breakInside = 'avoid';
        el.style.pageBreakInside = 'avoid';
    }

    if (ref.itemType === 'container') {
        el.style.flexWrap = 'wrap';
    }

    if (ref.itemType === 'container' || ref.itemType === 'widget') {
        el.style.display = 'flex';
        el.style.flex = `0 0 ${lts.width}`;
    }

    el.style.maxWidth = lts.width;
}

export function deflate(hdl: InflateHandle): void {
    if (!hdl) {
        return;
    }
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    hdl.sub && hdl.sub.unsubscribe();
    const refs = flatten<InflateRef>(
        hdl.refs,
        (e) => e.children,
        (e) => e.children,
        [],
    ).reverse();
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    refs.forEach((e) => e.ref && e.ref.destroy());
}
